import React, { useState, useEffect } from 'react';
import { getAdminToken, API_BASE_URL } from '../services/apiService';
import { FRONTEND_URL } from '../config/api';

const SiteSettings = () => {
    const [settings, setSettings] = useState({
        logo_url: '/assets/metalbox-logo-CMZone9_.png',
        logo_size: 'medium',
        title_size: 'medium',
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [uploadingLogo, setUploadingLogo] = useState(false);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/settings`);
            const data = await response.json();
            if (data.success) {
                setSettings(prev => ({ ...prev, ...data.settings }));
            }
        } catch (err) {
            console.error('Failed to fetch settings:', err);
            setError('无法加载设置');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setMessage('');
        setError('');

        try {
            const token = getAdminToken();
            const response = await fetch(`${API_BASE_URL}/admin/settings`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ settings }),
            });

            const data = await response.json();
            if (data.success) {
                setMessage('设置已保存');
            } else {
                setError(data.message || '保存失败');
            }
        } catch (err) {
            console.error('Failed to save settings:', err);
            setError('保存失败');
        } finally {
            setSaving(false);
        }
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // 验证文件大小 (30MB)
        if (file.size > 30 * 1024 * 1024) {
            setError('Logo 图片大小不能超过30MB');
            return;
        }

        const formData = new FormData();
        formData.append('logo', file);

        setUploadingLogo(true);
        try {
            const token = getAdminToken();
            const response = await fetch(`${API_BASE_URL}/admin/upload-logo`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: formData,
            });

            const data = await response.json();
            if (data.success) {
                setSettings(prev => ({ ...prev, logo_url: data.logoUrl }));
                setMessage('Logo 上传成功');
            } else {
                setError(data.message || 'Logo 上传失败');
            }
        } catch (err) {
            console.error('Failed to upload logo:', err);
            setError('Logo 上传失败');
        } finally {
            setUploadingLogo(false);
            // 清空 input value 以便允许重复上传同一文件
            e.target.value = '';
        }
    };

    if (loading) return <div className="text-white">加载中...</div>;

    return (
        <div className="bg-[#1a1d17] p-6 rounded-xl border border-[#2a2d27]">
            <h2 className="text-xl font-bold text-white mb-6">网站设置</h2>

            {message && (
                <div className="bg-green-500/10 border border-green-500/20 text-green-400 px-4 py-3 rounded-lg mb-6">
                    {message}
                </div>
            )}

            {error && (
                <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg mb-6">
                    {error}
                </div>
            )}

            <div className="space-y-8 max-w-2xl">
                {/* Logo Settings */}
                <div className="space-y-4">
                    <h3 className="text-lg font-medium text-white">Logo 设置</h3>

                    <div className="flex items-start gap-6">
                        <div className="w-32 h-32 bg-[#2a2d27] rounded-lg flex items-center justify-center overflow-hidden border border-[#3a3d37]">
                            {settings.logo_url ? (
                                <img src={`${FRONTEND_URL}${settings.logo_url}`} alt="Current Logo" className="max-w-full max-h-full object-contain" />
                            ) : (
                                <span className="text-gray-500">无 Logo</span>
                            )}
                        </div>

                        <div className="flex-1 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-2">上传新 Logo</label>
                                <div className="relative">
                                    <label className={`block w-full text-sm text-gray-400
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-full file:border-0
                    file:text-sm file:font-semibold
                    file:bg-[#4a5f3a] file:text-white
                    hover:file:bg-[#5a6f4a]
                    cursor-pointer ${uploadingLogo ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleLogoUpload}
                                            disabled={uploadingLogo}
                                            className="hidden"
                                        />
                                        <span className="inline-flex items-center px-4 py-2 rounded-full bg-[#4a5f3a] text-white text-sm font-semibold hover:bg-[#5a6f4a] transition-colors cursor-pointer">
                                            {uploadingLogo ? (
                                                <>
                                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    上传中...
                                                </>
                                            ) : (
                                                '选择文件'
                                            )}
                                        </span>
                                        <span className="ml-3">{uploadingLogo ? '正在处理...' : '未选择文件'}</span>
                                    </label>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-2">Logo 大小 (高度)</label>
                                <select
                                    value={settings.logo_size}
                                    onChange={(e) => setSettings({ ...settings, logo_size: e.target.value })}
                                    className="w-full bg-[#12140f] border border-[#2a2d27] rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#f5a623]"
                                >
                                    <option value="small">小 (h-6 / 24px)</option>
                                    <option value="medium">中 (h-8 / 32px)</option>
                                    <option value="large">大 (h-12 / 48px)</option>
                                    <option value="extra-large">特大 (h-16 / 64px)</option>
                                    <option value="super-large">超大 (h-20 / 80px)</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Title Settings */}
                <div className="space-y-4 pt-6 border-t border-[#2a2d27]">
                    <h3 className="text-lg font-medium text-white">标题设置</h3>

                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">标题文字大小</label>
                        <select
                            value={settings.title_size}
                            onChange={(e) => setSettings({ ...settings, title_size: e.target.value })}
                            className="w-full bg-[#12140f] border border-[#2a2d27] rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#f5a623]"
                        >
                            <option value="small">小 (text-lg)</option>
                            <option value="medium">中 (text-xl)</option>
                            <option value="large">大 (text-3xl)</option>
                            <option value="extra-large">特大 (text-4xl)</option>
                            <option value="super-large">超大 (text-5xl)</option>
                        </select>
                    </div>

                    <div className="p-4 bg-[#12140f] rounded-lg border border-[#2a2d27]">
                        <p className="text-sm text-gray-500 mb-2">预览:</p>
                        <div className="flex items-center gap-3">
                            {/* Preview Logic */}
                            <img
                                src={`${FRONTEND_URL}${settings.logo_url}`}
                                className={`w-auto object-contain ${settings.logo_size === 'small' ? 'h-6' :
                                        settings.logo_size === 'medium' ? 'h-8' :
                                            settings.logo_size === 'large' ? 'h-12' :
                                                settings.logo_size === 'extra-large' ? 'h-16' : 'h-20'
                                    }`}
                                alt="Preview"
                            />
                            <h2 className={`text-white font-bold leading-tight tracking-[-0.015em] ${settings.title_size === 'small' ? 'text-lg' :
                                    settings.title_size === 'medium' ? 'text-xl' :
                                        settings.title_size === 'large' ? 'text-3xl' :
                                            settings.title_size === 'extra-large' ? 'text-4xl' : 'text-5xl'
                                }`}>
                                Metalbox
                            </h2>
                        </div>
                    </div>
                </div>

                <div className="pt-6">
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className={`px-6 py-2 bg-[#4a5f3a] text-white font-bold rounded-lg hover:bg-[#5a6f4a] transition-colors ${saving ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                    >
                        {saving ? '保存中...' : '保存设置'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SiteSettings;
